import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MethodNode} from 'procedures/ProcedureDomainTypes';
import {ProcedureNodeType} from 'procedures/ProcedureDomainTypes';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {ProcessNodeForValidation} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';

export function isMethodNode(
  node: TaskDefinitionVersionSpec<unknown>
): node is TaskDefinitionVersionSpec<MethodNode> {
  return (
    (node.newProcessTaskDefinitionVersion.applicationData as MethodNode | undefined)?.type ===
    ProcedureNodeType.METHOD
  );
}

export function isNodeValidInProcedureFlowchart(node: TaskDefinitionVersionSpec<unknown>): boolean {
  // TODO(BNCH-136029): We can't fully validate here until the WF platform provides the task schema. Without it, we'll miss ROOT nodes
  return true;
}

export function isStaticMethodNode(node: StaticFlowchartNodeData): node is TaskSchemaStaticNodeData & {
  taskDefinitionVersionSpec: {
    newProcessTaskDefinitionVersion: {
      applicationData: MethodNode;
    };
  };
} {
  return (
    isStaticTaskSchemaNodeData(node) &&
    node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType ===
      ProcessExecutionType.PROCEDURE_METHOD
  );
}

export function extractMethodNodesFromStaticFlowchart(
  staticFlowchart: StaticTemplateFlowchart
): Array<MethodNode> {
  return staticFlowchart.nodesStaticData
    .filter((node) => isStaticTaskSchemaNodeData(node))
    .filter((node) => isMethodNode(node.taskDefinitionVersionSpec))
    .flatMap(
      (node) => node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData as MethodNode
    );
}

export function extractMethodNodeFromProcessNode(processNode: ProcessNodeForValidation): MethodNode | null {
  if (
    processNode.nodeType !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    processNode.executionType !== ProcessExecutionType.PROCEDURE_METHOD
  ) {
    return null;
  }

  const taskDefinitionVersionSpec = processNode.taskDefinitionVersionSpec;
  assert(
    isMethodNode(taskDefinitionVersionSpec),
    'Expected a task definition version spec to include a method node as application data'
  );
  return taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData;
}

/**
 * Field group keys used to categorize procedure fields.
 * Keep in sync with ProcedureFieldGroupKey in
 * benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
 */
export enum ProcedureFieldGroupKey {
  MATERIAL_INPUTS = 'MATERIAL_INPUTS',
  MATERIAL_OUTPUTS = 'MATERIAL_OUTPUTS',
  DATA_PARAMETERS = 'DATA_PARAMETERS',
  EQUIPMENT = 'EQUIPMENT',
}

/**
 * Keep in sync with the backend node adapter's fieldClientIdentifier format in
 * ParameterSettingToWorkflowFieldDefinitionMapper.resolve_field_client_identifier
 */
export function getFieldClientId(fieldGroupKey: ProcedureFieldGroupKey, fieldName: string): string {
  return `${fieldGroupKey}/${fieldName}`;
}
